import { createContext, PropsWithChildren, useEffect, useMemo, useRef } from 'react'

import { useDispatch, useSelector } from 'react-redux'
import { useRouteMatch } from 'react-router-dom'

import { IApplicationState } from '@store'
import { selectCampaignId, selectSubmissionId } from '@store/router'
import { fetchSubmission, ISubmission, selectSubmission } from '@store/submissions'
import { isEmpty } from '@utils'

interface ISubmissionProviderState {
  submission: ISubmission
}
export interface ISubmissionContextProps extends ISubmissionProviderState {
  isSubmissionFetched: boolean
}

export const SubmissionContext = createContext({} as ISubmissionContextProps)

// Base component for testing
export const SubmissionProvider: React.FC<
  PropsWithChildren<{
    submission: ISubmission
    isSubmissionFetched: boolean
    fetchSubmission: typeof fetchSubmission
    campaignId: number
    submissionId: number
  }>
> = ({
  children,
  submission,
  isSubmissionFetched,
  fetchSubmission: fetchSubmissionAction,
  campaignId,
  submissionId,
}) => {
  const calledRef = useRef(false)

  // Call fetchSubmission once on mount when submissionId and campaignId are present.
  useEffect(() => {
    if (submissionId && campaignId && !calledRef.current) {
      fetchSubmissionAction(submissionId, campaignId)
      calledRef.current = true
    }
  }, [submissionId, campaignId, fetchSubmissionAction])

  const contextProps: ISubmissionContextProps = useMemo(
    () => ({
      submission,
      isSubmissionFetched,
    }),
    [submission, isSubmissionFetched],
  )

  return <SubmissionContext.Provider value={contextProps}>{children}</SubmissionContext.Provider>
}

// Connected component using hooks
const ConnectedSubmissionProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const match = useRouteMatch()
  const dispatch = useDispatch()

  const campaignId = useSelector(() => selectCampaignId(match) as number)
  const submissionId = useSelector(() => selectSubmissionId(match) as number)
  const submission = useSelector((state: IApplicationState) =>
    selectSubmission(state, submissionId),
  )
  const isSubmissionFetched = !isEmpty(submission)

  return (
    <SubmissionProvider
      submission={submission}
      isSubmissionFetched={isSubmissionFetched}
      fetchSubmission={(sId, cId) => dispatch(fetchSubmission(sId, cId))}
      campaignId={campaignId}
      submissionId={submissionId}
    >
      {children}
    </SubmissionProvider>
  )
}

export default ConnectedSubmissionProvider
